import type { Gender, OrgSportOption } from '../types';
import { formatSportLabel } from './sportLabel';

export interface SportMismatch {
  /** What the fundraiser looks like it's for, e.g. "Girls Volleyball". */
  detectedLabel: string;
  /** The school's matching sport to switch to, when it has exactly one. */
  suggestion: OrgSportOption | null;
}

interface DetectInput {
  chosenSportId: string | null;
  sports: OrgSportOption[];
  /** The sport read off the fundraiser page, if any. */
  extractedSport?: string | null;
  /** The fundraiser's name as it currently stands in the form. */
  campaignName?: string | null;
}

type Signal = { kind: 'agree' } | { kind: 'mismatch'; mismatch: SportMismatch };

const GIRLS_WORDS = /\b(girls?|women'?s?|ladies|lady)\b/i;
const BOYS_WORDS = /\b(boys?|men'?s?)\b/i;
const GENDER_WORDS = /\b(girls?|women'?s?|ladies|lady|boys?|men'?s?|co-?ed)\b/gi;
const FILLER_WORDS = /\b(varsity|junior|jv|freshman|frosh|team|program|fundraiser|high school)\b/gi;

function normalize(text: string): string {
  return text.replace(/[‘’]/g, "'");
}

function genderHint(text: string): Gender | null {
  const girls = GIRLS_WORDS.test(text);
  const boys = BOYS_WORDS.test(text);
  if (girls === boys) return null;
  return girls ? 'GIRLS' : 'BOYS';
}

function sportPattern(sportName: string, flags = 'i'): RegExp {
  const escaped = sportName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, flags);
}

function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * The school's sport named `name` to switch to, if exactly one fits. A
 * boys/girls word rules out the other gender; the exact gender is preferred,
 * then co-ed.
 */
function pickSuggestion(
  name: string,
  hint: Gender | null,
  sports: OrgSportOption[],
  excludeId?: string,
): OrgSportOption | null {
  const sameNamed = sports.filter((s) => s.id !== excludeId && sameName(s.name, name));
  if (!hint) return sameNamed.length === 1 ? sameNamed[0] : null;
  const exact = sameNamed.filter((s) => s.gender === hint);
  if (exact.length > 0) return exact.length === 1 ? exact[0] : null;
  const coed = sameNamed.filter((s) => s.gender === 'COED');
  return coed.length === 1 ? coed[0] : null;
}

/** A mismatch toward `name`, suggesting the school's sport of that name when exactly one fits. */
function otherSportMismatch(name: string, hint: Gender | null, sports: OrgSportOption[]): Signal {
  const suggestion = pickSuggestion(name, hint, sports);
  const detectedLabel = suggestion
    ? formatSportLabel(suggestion.gender, suggestion.name)
    : formatSportLabel(hint ?? '', name);
  return { kind: 'mismatch', mismatch: { detectedLabel, suggestion } };
}

/** The chosen sport is named; only a boys/girls disagreement can still be a mismatch. */
function chosenSportSignal(chosen: OrgSportOption, hint: Gender | null, sports: OrgSportOption[]): Signal {
  if (chosen.gender === 'COED' || !hint || hint === chosen.gender) return { kind: 'agree' };
  const suggestion = pickSuggestion(chosen.name, hint, sports, chosen.id);
  return { kind: 'mismatch', mismatch: { detectedLabel: formatSportLabel(hint, chosen.name), suggestion } };
}

/**
 * The page-read sport is a sport name, not free text: once gender and filler
 * words are stripped, it must equal the chosen sport's name to agree. That
 * keeps "Flag Football" from agreeing with "Football" even when the school
 * has no Flag Football yet.
 */
function readExtractedSignal(rawText: string, chosen: OrgSportOption, sports: OrgSportOption[]): Signal | null {
  const text = normalize(rawText);
  const name = text
    .replace(GENDER_WORDS, '')
    .replace(FILLER_WORDS, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!name) return null;
  const hint = genderHint(text);
  if (sameName(name, chosen.name)) return chosenSportSignal(chosen, hint, sports);
  return otherSportMismatch(name, hint, sports);
}

/**
 * Reads the fundraiser name on its own. Longer sport names win over
 * names they contain ("Flag Football" is not "Football"), and the gender hint
 * comes only from this text.
 */
function readNameSignal(rawText: string, chosen: OrgSportOption, sports: OrgSportOption[]): Signal | null {
  const text = normalize(rawText);
  if (!text.trim()) return null;
  const hint = genderHint(text);

  const mentioned = sports
    .filter((s) => sportPattern(s.name).test(text))
    .sort((a, b) => b.name.length - a.name.length);

  // Blank out longer names that contain the chosen one, so "Flag Football"
  // can't vouch for "Football".
  let chosenText = text;
  for (const s of mentioned) {
    if (s.name.length > chosen.name.length && sportPattern(chosen.name).test(s.name)) {
      chosenText = chosenText.replace(sportPattern(s.name, 'gi'), ' ');
    }
  }

  if (sportPattern(chosen.name).test(chosenText)) return chosenSportSignal(chosen, hint, sports);
  return mentioned[0] ? otherSportMismatch(mentioned[0].name, hint, sports) : null;
}

/**
 * Compares what a fundraiser says it's for against the sport it's being filed
 * under. The page-read sport is the more specific signal, so it decides when
 * present; the fundraiser name is the fallback. Returns null when they agree
 * or nothing names a sport — this is a nudge, so silence is the safe default.
 */
export function detectSportMismatch({
  chosenSportId,
  sports,
  extractedSport,
  campaignName,
}: DetectInput): SportMismatch | null {
  const chosen = sports.find((s) => s.id === chosenSportId);
  if (!chosen) return null;

  const signal =
    readExtractedSignal(extractedSport ?? '', chosen, sports) ??
    readNameSignal(campaignName ?? '', chosen, sports);

  return signal?.kind === 'mismatch' ? signal.mismatch : null;
}
